import { useState } from "react";
import { useNotify, useRefresh, useTranslate, useUpdate } from "ra-core";
import { Confirm } from "@/components/admin/confirm";

import type { Tag } from "../types";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { TagDialog } from "./TagDialog";

type TagEditModalProps = {
  tag: Tag;
  open: boolean;
  onClose(): void;
  onSuccess?(tag: Tag): Promise<void>;
};

export function TagEditModal({
  tag,
  open,
  onClose,
  onSuccess,
}: TagEditModalProps) {
  const [update] = useUpdate<Tag>();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEditTag = async (data: Pick<Tag, "name" | "color">) => {
    await update(
      "tags",
      { id: tag.id, data, previousData: tag },
      {
        onSuccess: async (tag) => {
          await onSuccess?.(tag);
        },
      },
    );
  };

  const handleDeleteTag = async () => {
    setIsDeleting(true);
    try {
      // Removes the tag from every contact/company that has it, then
      // deletes it - a plain dataProvider.delete("tags") would leave
      // dangling ids behind in every one of those records' tags arrays.
      const { error } = await getSupabaseClient().rpc("delete_tag", {
        target: tag.id,
      });
      if (error) throw error;
      setConfirmingDelete(false);
      onClose();
      refresh();
    } catch (e) {
      notify(
        e instanceof Error ? e.message : "Couldn't delete this tag.",
        { type: "error" },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Confirm
        isOpen={confirmingDelete}
        title="Delete this tag?"
        content={`"${tag.name}" will be removed from every contact and company that has it. This can't be undone.`}
        confirm="Delete"
        confirmColor="warning"
        onConfirm={handleDeleteTag}
        onClose={() => setConfirmingDelete(false)}
      />
      <TagDialog
        open={open}
        title={translate("resources.tags.dialog.edit_title")}
        onClose={onClose}
        onSubmit={handleEditTag}
        onDelete={() => setConfirmingDelete(true)}
        isDeleting={isDeleting}
        tag={tag}
      />
    </>
  );
}
